/**
 * Generating the pack: which artefact, from which version, on which template.
 *
 * The controls were three rows of selects wedged into the snapshot's header.
 * A dialog is what they are — a short decision taken once, then a file.
 *
 * @module components/submission/ExportDialog
 */

import React, { useState } from "react";

import { Alert, Button, Modal, MutedText, RadioCardGroup, WarningAlert } from "@/components/ui";
import { FormField, SelectField } from "@/components/ui/Form";
import { DESCRIPTOR_TEMPLATES } from "@/export/descriptor-template-registry";
import type { DescriptorVariant } from "@/export/descriptor-variant";
import { resolveDescriptorVariant } from "@/export/descriptor-variant";
import { resolveProgrammeLeadContact } from "@/export/programme-lead";
import { exportProgrammeDescriptorWord } from "@/export/word";
import { useOptionalWorkspaceContext } from "@/router/workspace-context";
import { can } from "@/server/authorization";
import type { Programme } from "@/types";
import { downloadBlob } from "@/utils/dom";
import { withTenantProviderSettings } from "@/utils/provider-settings";

type Artefact = "word" | "json";

const ARTEFACTS = [
  {
    value: "word" as const,
    label: "Programme descriptor",
    description: "A Word pack that uses the QQI template for the award type.",
    icon: "file-doc",
    "data-testid": "export-artefact-word",
  },
  {
    value: "json" as const,
    label: "Programme JSON",
    description: "The full programme design, for backup or use in another tool.",
    icon: "brackets-curly",
    "data-testid": "export-artefact-json",
  },
];

const TEMPLATE_OPTIONS = [
  { value: "non-cas", label: "Higher Education (non-CAS)" },
  { value: "cas", label: "QQI FET / CAS" },
];

export interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  programme: Programme;
  /**
   * Whether the server holds the programme ready for final export. Anything
   * less exports as a draft, and `undefined` means readiness is not known: the
   * pack is still a draft, but nothing here may say the programme is not ready.
   */
  isComplete: boolean | undefined;
  /** Readiness is unknown because its read failed, rather than not yet answered. */
  readinessFailed?: boolean;
  /** A generated preview was recorded, so a list of artefacts can read again. */
  onPreviewRecorded?: () => void;
}

export function ExportDialog({
  open,
  onClose,
  programme,
  isComplete,
  readinessFailed = false,
  onPreviewRecorded = undefined,
}: ExportDialogProps): React.ReactElement {
  const workspace = useOptionalWorkspaceContext();
  // A change's working copy is not the programme, so its previews are not
  // recorded; nor is a reader's, who neither reviews nor edits it.
  const record = workspace?.editorSync.record;
  const resource = { type: "programme", programmeId: record?.id ?? "" } as const;
  const recordedProgrammeId =
    record &&
    !record.changeCaseId &&
    (can(workspace.subject, "programme:review", resource) ||
      can(workspace.subject, "programme:write", resource))
      ? record.id
      : undefined;
  const [artefact, setArtefact] = useState<Artefact>("word");
  const [variant, setVariant] = useState<DescriptorVariant>(() =>
    resolveDescriptorVariant(programme),
  );
  const [versionId, setVersionId] = useState<string>(() => programme.versions?.[0]?.id ?? "");
  const [blockers, setBlockers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  // What the last export produced. A download gives no sign of its own inside
  // the page, so the dialog says it happened.
  const [done, setDone] = useState("");

  const template = DESCRIPTOR_TEMPLATES[variant];
  const versions = programme.versions ?? [];

  const exportWord = async (): Promise<void> => {
    setError(null);
    setDone("");
    setExporting(true);
    try {
      const programmeLead =
        workspace && record
          ? await resolveProgrammeLeadContact(workspace.client, record.id, workspace.platformUsers)
          : undefined;
      const result = await exportProgrammeDescriptorWord(
        withTenantProviderSettings(programme, workspace?.tenantProviderSettings),
        variant,
        versionId || undefined,
        programmeLead,
      );
      setBlockers(result.exportBlockers ?? []);
      setDone(isComplete ? "Descriptor downloaded." : "Draft descriptor downloaded.");
      if (workspace && recordedProgrammeId) {
        await workspace.client
          .recordExportPreview({
            programmeId: recordedProgrammeId,
            format: "descriptor",
            template: template.friendlyName,
            ...(versionId ? { versionId } : {}),
          })
          .then(() => onPreviewRecorded?.())
          .catch(() => {
            setError(
              "The descriptor downloaded, but its preview could not be recorded in Artefacts.",
            );
          });
      }
    } catch (thrown) {
      // Reported here rather than through `alert`, which stops the page and
      // takes the dialog's own state with it.
      setError((thrown as Error)?.message || String(thrown));
    } finally {
      setExporting(false);
    }
  };

  const copyJson = async (): Promise<void> => {
    setError(null);
    try {
      await navigator.clipboard.writeText(JSON.stringify(programme, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (thrown) {
      setError((thrown as Error)?.message || String(thrown));
    }
  };

  const downloadJson = (): void => {
    const filename = `${(programme.title ?? "programme").replace(/[^a-z0-9]/gi, "_")}-design.json`;
    downloadBlob(
      new Blob([JSON.stringify(programme, null, 2)], { type: "application/json" }),
      filename,
    );
    setDone("Programme JSON downloaded.");
  };

  // Word's blockers and error describe that export specifically; carrying
  // them into the JSON tab implied the JSON dump was also an incomplete
  // preview with outstanding submission items, which is not true of it.
  const changeArtefact = (next: Artefact): void => {
    setArtefact(next);
    setBlockers([]);
    setError(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate pack"
      subtitle={programme.title || "Untitled programme"}
      size="lg"
      data-testid="submission-export-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {artefact === "word" ? (
            <Button
              variant="primary"
              icon="file-doc"
              loading={exporting}
              loadingLabel="Exporting"
              onClick={exportWord}
              data-testid="snapshot-export-word"
            >
              Export descriptor
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                icon="copy"
                onClick={copyJson}
                data-testid="snapshot-copy-json"
              >
                {copied ? "Copied" : "Copy JSON"}
              </Button>
              <Button
                variant="primary"
                icon="download-simple"
                onClick={downloadJson}
                data-testid="snapshot-download-json"
              >
                Download JSON
              </Button>
            </>
          )}
        </>
      }
    >
      <RadioCardGroup
        legend="Artefact to generate"
        options={ARTEFACTS}
        value={artefact}
        onChange={changeArtefact}
        columns={2}
      />
      {artefact === "word" ? (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <FormField label="Descriptor template" htmlFor="export-template">
            <SelectField
              id="export-template"
              value={variant}
              options={TEMPLATE_OPTIONS}
              onChange={(value) => setVariant(value as DescriptorVariant)}
              data-testid="snapshot-export-template-select"
            />
          </FormField>
          {versions.length > 0 ? (
            <FormField label="Programme version" htmlFor="export-version">
              <SelectField
                id="export-version"
                value={versionId}
                options={versions.map((version) => ({
                  value: version.id,
                  label: version.label ?? version.code ?? "Version",
                }))}
                onChange={setVersionId}
                data-testid="snapshot-export-version-select"
              />
            </FormField>
          ) : null}
        </div>
      ) : null}
      {artefact === "word" ? (
        <MutedText className="mt-3" data-testid="snapshot-export-template">
          Template: <span data-testid="snapshot-export-template-name">{template.friendlyName}</span>{" "}
          <span className="font-mono" data-testid="snapshot-export-template-file">
            {template.fileName}
          </span>
        </MutedText>
      ) : null}
      {artefact === "word" && isComplete === undefined ? (
        <MutedText className="mt-3" data-testid="snapshot-export-partial-note">
          {readinessFailed
            ? "This pack is a draft because the programme's readiness could not be checked."
            : "This pack is a draft because the programme's readiness has not been checked yet."}
        </MutedText>
      ) : null}
      {artefact === "word" && isComplete === false ? (
        <MutedText className="mt-3" data-testid="snapshot-export-partial-note">
          This pack is a draft because the programme is not yet ready for final export.
        </MutedText>
      ) : null}
      {/* Mounted before it has anything to say, so the news is announced. */}
      <div role="status" className="mt-4 empty:hidden" data-testid="snapshot-export-status">
        {done ? <Alert tone="success">{done}</Alert> : null}
      </div>
      {error ? (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {artefact === "word" && blockers.length > 0 ? (
        <div className="mt-4" data-testid="snapshot-export-blockers">
          <WarningAlert title="Remaining items to complete before submission:">
            <ul className="mt-1 list-disc ps-5 text-md">
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </WarningAlert>
        </div>
      ) : null}
    </Modal>
  );
}

export default ExportDialog;
